import { useState, useEffect } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiCalendar, FiClock, FiGrid, FiList, FiArrowUpCircle, FiUsers, FiTag, FiLogOut,
  FiFileText, FiPercent, FiSettings, FiShield, FiCreditCard, FiMenu, FiX, FiExternalLink, FiCode
} from 'react-icons/fi';
import { LuCalendarDays, LuCrown } from 'react-icons/lu';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useLocale } from '../contexts/LocaleContext.jsx';
import { BOOKING_BASE_URL } from '../lib/config.js';
import EmployeePasswordGate from './EmployeePasswordGate.jsx';

// Dashboard shell with nav (architecture.md §19).
export default function Layout() {
  const { profile, isSuperAdmin, isEmployee, canManageTeam, mustResetPassword, logout } = useAuth();
  const { locale, setLocale } = useLocale();
  const { t } = useTranslation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();

  // Close sidebar on navigation (mobile)
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile sidebar is open
  useEffect(() => {
    if (isSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isSidebarOpen]);

  const linkClass = ({ isActive }) =>
    `group relative flex items-center gap-3 rounded-dc-sm px-3.5 py-2.5 text-xs font-semibold transition-all duration-200 ${
      isActive 
        ? 'bg-slate-900 text-white shadow-md shadow-slate-900/15 translate-x-1' 
        : 'text-slate-600 hover:bg-slate-100/80 hover:text-slate-900 hover:translate-x-1'
    }`;

  const navIconClass = (isActive) =>
    `h-4 w-4 transition-transform duration-200 ${isActive ? 'text-brand-400 scale-110' : 'text-slate-400 group-hover:text-slate-700'}`;

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 font-sans">
      {/* Mobile Top Header */}
      <div className="fixed top-0 left-0 right-0 z-40 flex h-16 items-center justify-between border-b border-slate-200/80 bg-white/90 backdrop-blur-md px-4 md:hidden">
        <div className="flex items-center">
          <img src="/Day cal logo name.png" alt="DayCal" className="h-14 w-auto -ml-3 -my-3 object-contain" />
        </div>
        <button
          onClick={() => setIsSidebarOpen(true)}
          className="rounded-dc-sm p-2 text-slate-600 hover:bg-slate-100 focus:outline-none"
        >
          <FiMenu className="h-6 w-6" />
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/60 backdrop-blur-xs md:hidden transition-opacity"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-slate-200/80 bg-white/95 backdrop-blur-xl px-4 py-5 shadow-2xl shadow-slate-900/5 transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0 ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Sidebar Header Brand */}
        <div className="mb-6 flex items-center justify-between px-2 pt-1">
          <div className="flex items-center">
            <img src="/Day cal logo name.png" alt="DayCal" className="h-16 w-auto -ml-3 -my-3 object-contain transition-transform duration-500 hover:scale-105" />
          </div>
          {/* Mobile Close Button */}
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="rounded-dc-sm p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 md:hidden"
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>
        
        {/* Nav Links */}
        <nav className="flex flex-1 flex-col gap-5 overflow-y-auto pr-1 custom-scrollbar">
          {/* Employee navigation */}
          {isEmployee && (
            <div className="space-y-1">
              <p className="px-3 text-[10px] font-extrabold uppercase tracking-wider text-slate-400 mb-2">My Desk</p>
              <NavLink to="/availability" className={linkClass}>
                {({ isActive }) => <><FiClock className={navIconClass(isActive)} /> <span>{t('dashboard.availability')}</span></>}
              </NavLink>
              <NavLink to="/bookings" className={linkClass}>
                {({ isActive }) => <><FiList className={navIconClass(isActive)} /> <span>{t('dashboard.bookings')}</span></>}
              </NavLink>
              <NavLink to="/booking-calendar" className={linkClass}>
                {({ isActive }) => <><LuCalendarDays className={navIconClass(isActive)} /> <span>{t('dashboard.bookingCalendar')}</span></>}
              </NavLink>
            </div>
          )}

          {/* Host / Creator Navigation */}
          {!isSuperAdmin && !isEmployee && (
            <>
              {/* Category 1: Main */}
              <div className="space-y-1">
                <p className="px-3 text-[10px] font-extrabold uppercase tracking-wider text-slate-400 mb-2">Main Menu</p>
                <NavLink to="/dashboard" end className={linkClass}>
                  {({ isActive }) => <><FiGrid className={navIconClass(isActive)} /> <span>{t('dashboard.overview')}</span></>}
                </NavLink>
                <NavLink to="/event-types" className={linkClass}>
                  {({ isActive }) => <><FiCalendar className={navIconClass(isActive)} /> <span>{t('dashboard.eventTypes')}</span></>}
                </NavLink>
                <NavLink to="/bookings" className={linkClass}>
                  {({ isActive }) => <><FiList className={navIconClass(isActive)} /> <span>{t('dashboard.bookings')}</span></>}
                </NavLink>
                <NavLink to="/booking-calendar" className={linkClass}>
                  {({ isActive }) => <><LuCalendarDays className={navIconClass(isActive)} /> <span>{t('dashboard.bookingCalendar')}</span></>}
                </NavLink>
              </div>

              {/* Category 2: Management */}
              <div className="space-y-1">
                <p className="px-3 text-[10px] font-extrabold uppercase tracking-wider text-slate-400 mb-2">Scheduling Tools</p>
                <NavLink to="/availability" className={linkClass}>
                  {({ isActive }) => <><FiClock className={navIconClass(isActive)} /> <span>{t('dashboard.availability')}</span></>}
                </NavLink>
                <NavLink to="/form" className={linkClass}>
                  {({ isActive }) => <><FiFileText className={navIconClass(isActive)} /> <span>{t('dashboard.formBuilder')}</span></>}
                </NavLink>
                {canManageTeam && (
                  <NavLink to="/team" className={linkClass}>
                    {({ isActive }) => (
                      <>
                        <FiUsers className={navIconClass(isActive)} />
                        <span>Team</span>
                        <LuCrown className="ml-auto h-3.5 w-3.5 text-amber-500 animate-pulse" title="Premium feature" />
                      </>
                    )}
                  </NavLink>
                )}
                <NavLink to="/promo-codes" className={linkClass}>
                  {({ isActive }) => <><FiPercent className={navIconClass(isActive)} /> <span>{t('dashboard.promoCodes')}</span></>}
                </NavLink>
              </div>

              {/* Category 3: System */}
              <div className="space-y-1">
                <p className="px-3 text-[10px] font-extrabold uppercase tracking-wider text-slate-400 mb-2">Account</p>
                <NavLink to="/settings" className={linkClass}>
                  {({ isActive }) => <><FiSettings className={navIconClass(isActive)} /> <span>{t('dashboard.settings')}</span></>}
                </NavLink>
                <NavLink to="/upgrade" className={linkClass}>
                  {({ isActive }) => {
                    const rawPlan = profile?.plan_name || profile?.plan_id || 'Free';
                    const planName = rawPlan.split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
                    return (
                      <>
                        <FiArrowUpCircle className={navIconClass(isActive)} />
                        <span>{t('dashboard.upgradePlan')}</span>
                        <span
                          className={`ml-auto inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wide border transition-all ${
                            isActive
                              ? 'bg-brand-400/20 text-brand-300 border-brand-400/40 shadow-xs'
                              : 'bg-gradient-to-r from-brand-50 to-indigo-50 text-brand-700 border-brand-200/80 shadow-2xs group-hover:from-brand-100 group-hover:to-indigo-100 group-hover:border-brand-300'
                          }`}
                        >
                          {planName}
                        </span>
                      </>
                    );
                  }}
                </NavLink>
              </div>
            </>
          )}

          {/* Super Admin Navigation */}
          {isSuperAdmin && (
            <>
              <div className="space-y-1">
                <p className="px-3 text-[10px] font-extrabold uppercase tracking-wider text-slate-400 mb-1.5 flex items-center justify-between">
                  <span>Accounts & Plans</span>
                  <span className="rounded-full bg-slate-900 px-2 py-0.5 text-[9px] font-extrabold text-white">ADMIN</span>
                </p>
                <NavLink to="/admin/users" className={linkClass}>
                  {({ isActive }) => <><FiUsers className={navIconClass(isActive)} /> <span>{t('admin.users')}</span></>}
                </NavLink>
                <NavLink to="/admin/plans" className={linkClass}>
                  {({ isActive }) => <><FiTag className={navIconClass(isActive)} /> <span>{t('admin.plans')}</span></>}
                </NavLink>
                <NavLink to="/admin/promo-codes" className={linkClass}>
                  {({ isActive }) => <><FiPercent className={navIconClass(isActive)} /> <span>{t('admin.platformPromos')}</span></>}
                </NavLink>
              </div>

              <div className="space-y-1">
                <p className="px-3 text-[10px] font-extrabold uppercase tracking-wider text-slate-400 mb-1.5">Platform Operations</p>
                <NavLink to="/admin/bookings" className={linkClass}>
                  {({ isActive }) => <><FiList className={navIconClass(isActive)} /> <span>{t('admin.globalAppointments')}</span></>}
                </NavLink>
                <NavLink to="/admin/payments" className={linkClass}>
                  {({ isActive }) => <><FiCreditCard className={navIconClass(isActive)} /> <span>{t('admin.paymentConfig')}</span></>}
                </NavLink>
              </div>

              <div className="space-y-1">
                <p className="px-3 text-[10px] font-extrabold uppercase tracking-wider text-slate-400 mb-1.5">System & Security</p>
                <NavLink to="/admin/settings" className={linkClass}>
                  {({ isActive }) => <><FiSettings className={navIconClass(isActive)} /> <span>{t('admin.platformSettings')}</span></>}
                </NavLink>
                <NavLink to="/admin/audit" className={linkClass}>
                  {({ isActive }) => <><FiShield className={navIconClass(isActive)} /> <span>{t('admin.auditLogs')}</span></>}
                </NavLink>
              </div>

              <div className="space-y-1">
                <p className="px-3 text-[10px] font-extrabold uppercase tracking-wider text-slate-400 mb-1.5">Content</p>
                <NavLink to="/admin/legal-pages" className={linkClass}>
                  {({ isActive }) => <><FiFileText className={navIconClass(isActive)} /> <span>Legal Pages</span></>}
                </NavLink>
              </div>

              <div className="space-y-1">
                <p className="px-3 text-[10px] font-extrabold uppercase tracking-wider text-slate-400 mb-1.5">Developers</p>
                <NavLink to="/admin/api-docs" className={linkClass}>
                  {({ isActive }) => <><FiCode className={navIconClass(isActive)} /> <span>API Docs</span></>}
                </NavLink>
              </div>
            </>
          )}
        </nav>

        {/* Sidebar Footer & User Profile Card */}
        <div className="mt-auto pt-4 space-y-3">
          {!isSuperAdmin && !isEmployee && (
            <a 
              href={`${BOOKING_BASE_URL}/u/${profile?.username}`} 
              target="_blank" 
              rel="noreferrer" 
              className="flex items-center justify-center gap-2 rounded-dc-sm bg-gradient-to-r from-slate-900 to-slate-800 px-3.5 py-2.5 text-xs font-semibold text-white shadow-md shadow-slate-900/10 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
            >
              <FiExternalLink className="h-3.5 w-3.5 text-brand-400" /> 
              <span>View Booking Page</span>
            </a>
          )}

          {/* User Info Container Card */}
          <div className="rounded-dc-sm border border-slate-200/80 bg-slate-50/80 p-3 shadow-xs flex items-center justify-between">
            <div className="flex items-center gap-2.5 truncate pr-1">
              <div className="relative shrink-0">
                <span className="grid h-8 w-8 place-items-center rounded-dc-sm bg-slate-700 text-xs font-bold text-white shadow-xs">
                  {(profile?.name || profile?.username || 'U').charAt(0).toUpperCase()}
                </span>
                <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white" />
              </div>
              <div className="flex flex-col truncate">
                <span className="text-xs font-bold text-slate-900 truncate">{profile?.name || profile?.username || 'User'}</span>
                <span className="text-[10px] text-slate-400 truncate">/u/{profile?.username || 'user'}</span>
              </div>
            </div>
            
            <button 
              type="button" 
              onClick={logout} 
              title="Sign out"
              className="p-1.5 rounded-dc-sm text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-colors shrink-0"
            >
              <FiLogOut className="h-4 w-4" />
            </button>
          </div>
          
          {/* Language Switcher Pills */}
          <div className="flex items-center justify-between px-1 text-[11px] font-semibold text-slate-400">
            <span>Language</span>
            <div className="flex items-center rounded-full bg-slate-100 p-0.5 text-[10px]">
              <button 
                type="button" 
                onClick={() => setLocale('en')} 
                className={`rounded-full px-2 py-0.5 transition-colors ${locale === 'en' ? 'bg-slate-900 text-white shadow-xs' : 'text-slate-500 hover:text-slate-800'}`}
              >
                ENG
              </button>
              <button 
                type="button" 
                onClick={() => setLocale('bn')} 
                className={`rounded-full px-2 py-0.5 transition-colors ${locale === 'bn' ? 'bg-slate-900 text-white shadow-xs' : 'text-slate-500 hover:text-slate-800'}`}
              >
                BN
              </button>
            </div>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-auto pt-20 pb-6 px-4 md:pt-6 md:p-10 w-full">
        <div className="mx-auto max-w-6xl w-full">
          <Outlet />
        </div>
      </main>

      {/* Forced first-login password reset for agency employees (PRD §8.2). */}
      {mustResetPassword && <EmployeePasswordGate />}
    </div>
  );
}
